import { Grid, html } from "gridjs";
import "gridjs/dist/theme/mermaid.css";
import { uiState } from "../../state/uiState.js";
import { addToBarcodeCart } from "./dialogs/barcodeCartDialog.js";
import { canUseBarcodeCart } from "./barcodeCartPermissions.js";

let demirbasGrid = null;
let didBindGridEvents = false;
let currentDemirbasRows = new Map();

function destroyDemirbasGridInstance() {
	if (!demirbasGrid) return;
	demirbasGrid.destroy();
	demirbasGrid = null;
}

function escapeHtml(value) {
	return String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function ellipsisCell(cell, className = "") {
	const text = cell === null || cell === undefined || cell === "" ? "\u2014" : String(cell);
	const safe = escapeHtml(text);
	const cls = ["grid-cell-ellipsis", className].filter(Boolean).join(" ");
	return html(`<span class="${cls}" title="${safe}">${safe}</span>`);
}

function formatTryAmount(value) {
	const amount = Number(value ?? 0);
	const safeAmount = Number.isFinite(amount) ? amount : 0;
	return `${safeAmount.toLocaleString("tr-TR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})} \u20BA`;
}

function formatGridDate(value, withTime = false) {
	if (!value) return "\u2014";
	if (!withTime && typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
		const [year, month, day] = value.split("-");
		return `${day}.${month}.${year}`;
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);

	const dateText = date.toLocaleDateString("tr-TR");
	if (!withTime) return dateText;

	return `${dateText} ${date.toLocaleTimeString("tr-TR", {
		hour: "2-digit",
		minute: "2-digit",
	})}`;
}

function toSortableDateValue(value) {
	if (!value) return Number.NEGATIVE_INFINITY;
	const time = new Date(value).getTime();
	return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

function getShortLabel(row) {
	const full = `${row?.marka ?? ""} ${row?.model ?? ""}`.trim();
	if (full) return full;
	return row?.urun_cinsi ?? row?.urun_alt_grubu ?? row?.urun_grubu ?? "Urun";
}

export function initDemirbasGridEvents() {
	if (didBindGridEvents) return;
	didBindGridEvents = true;

	document.body.addEventListener("change", (event) => {
		if (event.target && event.target.matches("select.per-page")) {
			const value = Number(event.target.value);
			uiState.demirbas.paginationLimit = value;
			rerenderDemirbasGridWithLastUrl(value);
		}
	});

	document.body.addEventListener("click", (e) => {
		const viewBtn = e.target.closest(".grid-action-btn--view");
		const editBtn = e.target.closest(".grid-action-btn--edit");
		const cartBtn = e.target.closest(".grid-action-btn--cart");

		if (viewBtn) window.openOverviewDialog?.(viewBtn.dataset.id);
		if (editBtn) window.openEditDialog?.(editBtn.dataset.id);
		if (cartBtn) addToBarcodeCart(currentDemirbasRows.get(cartBtn.dataset.id));
	});
}

function buildDemirbasGridConfig({ url, paginationLimit }) {
	const col = (id, name, minWidth, className = "grid-cell-ellipsis--wide") => ({
		id,
		name: html(`<div style="margin-top:3px;">${name}</div>`),
		minWidth,
		formatter: (cell) => ellipsisCell(cell, className),
	});

	const tryAmountCol = (id, name, minWidth) => ({
		id,
		name: html(`<div style="margin-top:3px;">${name}</div>`),
		minWidth,
		sort: {
			compare: (a, b) => {
				const left = Number(a ?? 0);
				const right = Number(b ?? 0);
				return left - right;
			},
		},
		formatter: (cell) => ellipsisCell(formatTryAmount(cell), "grid-cell-ellipsis--wide"),
	});

	const dateCol = (id, name, minWidth, { withTime = false, className = "grid-cell-ellipsis--wide" } = {}) => ({
		id,
		name: html(`<div style="margin-top:3px;">${name}</div>`),
		minWidth,
		sort: {
			compare: (a, b) => toSortableDateValue(a) - toSortableDateValue(b),
		},
		formatter: (cell) => ellipsisCell(formatGridDate(cell, withTime), className),
	});

	return {
		columns: [
			col("demirbas_id", "Urun Kodu", "160px"),
			col("departman", "Departman", "160px"),
			col("urun_grubu", "Urun Grubu", "160px"),
			col("urun_alt_grubu", "Urun Alt Grubu", "185px"),
			col("urun_cinsi", "Urun Cinsi", "150px"),
			col("marka", "Marka", "150px"),
			col("model", "Model", "150px"),
			col("lokasyon", "Lokasyon", "150px"),
			col("oda", "Oda", "150px"),
			col("yer", "Yer", "150px"),
			col("seri_numarasi", "Seri Numarasi", "150px"),
			col("barkod", "Barkod", "150px"),
			col("fatura_paket_id", "Fatura Paket", "160px"),
			tryAmountCol("toplam_satin_alma_degeri_try", "Toplam Satin Alma Degeri", "190px"),
			col("urun_durumu", "Urun Durumu", "150px", "grid-cell-ellipsis--xwide"),
			col("zimmet_durumu", "Zimmet Durumu", "150px", "grid-cell-ellipsis--xwide"),
			dateCol("zimmet_tarihi", "Zimmet Tarihi", "150px"),
			col("zimmetlenen_kisi", "Zimmetlenen Kisi", "180px"),
			col("olusturan_kullanici", "Olusturan Kullanici", "180px", "grid-cell-ellipsis--xxwide"),
			dateCol("olusturma_tarihi", "Olusturma Tarihi", "160px", {
				withTime: true,
				className: "grid-cell-ellipsis--xxwide",
			}),
			col("son_guncelleyen_kullanici", "Son Guncelleyen Kullanici", "200px", "grid-cell-ellipsis--xxwide"),
			dateCol("son_guncelleme_tarihi", "Son Guncelleme Tarihi", "180px", {
				withTime: true,
				className: "grid-cell-ellipsis--xxwide",
			}),
			col("aciklama", "Aciklama", "200px", ""),
			{
				id: "aksiyon",
				name: html('<div style="cursor:default; text-align:center;">Islemler</div>'),
				sort: false,
				formatter: (_cell, row) => {
					const id = row?.cells?.[0]?.data ?? "";
					const canEdit = ["admin", "kullanici"].includes(uiState.currentUser?.yetki);
					const canUseCart = canUseBarcodeCart(uiState.currentUser);
					return html(`
						<div class="grid-actions">
							<button class="grid-action-btn grid-action-btn--view" data-id="${id}" title="Goruntule">
								<span class="material-icons">visibility</span>
							</button>
							${canEdit ? `<button class="grid-action-btn grid-action-btn--edit" data-id="${id}" title="Duzenle">
								<span class="material-icons">edit</span>
							</button>` : ""}
							${canUseCart ? `<button class="grid-action-btn grid-action-btn--cart" data-id="${id}" title="Barkod Sepetine Ekle">
								<span class="material-icons">add_shopping_cart</span>
							</button>` : ""}
						</div>
					`);
				},
				attributes: {
					style: "position: sticky; right: 0; padding: 8px 12px; min-width: 170px;",
				},
			},
		],

		server: {
			url,
			then: (data) => {
				const total = data.count ?? 0;
				currentDemirbasRows = new Map((data.items || []).map((row) => [
					String(row.demirbas_id),
					{
						demirbas_id: String(row.demirbas_id),
						barkod: String(row.barkod ?? ""),
						shortLabel: getShortLabel(row),
					},
				]));
				const countEl = document.getElementById("demirbas-count");
				if (countEl) countEl.textContent = `${total} kayit`;
				return (data.items || []).map((row) => [
					row.demirbas_id,
					row.departman,
					row.urun_grubu,
					row.urun_alt_grubu,
					row.urun_cinsi,
					row.marka,
					row.model,
					row.lokasyon,
					row.oda,
					row.yer,
					row.seri_numarasi,
					row.barkod,
					row.fatura_paket_id,
					row.toplam_satin_alma_degeri_try,
					row.urun_durumu,
					row.zimmet_durumu,
					row.zimmet_tarihi,
					row.zimmetlenen_kisi,
					row.olusturan_kullanici,
					row.olusturma_tarihi,
					row.son_guncelleyen_kullanici,
					row.son_guncelleme_tarihi,
					row.aciklama,
					null,
				]);
			},
			total: (data) => data.count ?? 0,
		},

		autoWidth: false,
		search: false,
		sort: true,
		pagination: { limit: paginationLimit },
		fixedHeader: false,

		style: {
			container: { padding: "0px" },
			footer: { "border-radius": "0px", "box-shadow": "none" },
			header: { padding: "10px 20px" },
			table: { "white-space": "nowrap", "font-size": "11px", "margin-bottom": "3px" },
			th: { padding: "8px 16px" },
			td: { padding: "16px 16px" },
		},
	};
}

export function renderDemirbasGrid({ url, paginationLimit = 10 }) {
	uiState.demirbas.lastSearchUrl = url;
	uiState.demirbas.paginationLimit = paginationLimit;

	const tableGrid = document.querySelector(".table-grid");
	if (!tableGrid) {
		console.warn(".table-grid bulunamadi, grid render edilmedi.");
		return;
	}

	const config = buildDemirbasGridConfig({ url, paginationLimit });

	destroyDemirbasGridInstance();
	tableGrid.innerHTML = "";
	demirbasGrid = new Grid(config).render(tableGrid);

	initGridFooter();
}

export function rerenderDemirbasGridWithLastUrl(paginationLimit) {
	const lastUrl = uiState.demirbas.lastSearchUrl;
	if (!lastUrl) return;
	renderDemirbasGrid({ url: lastUrl, paginationLimit });
}

export function clearDemirbasGrid() {
	destroyDemirbasGridInstance();
	currentDemirbasRows = new Map();
	const tableGrid = document.querySelector(".table-grid");
	if (tableGrid) tableGrid.innerHTML = "";
	uiState.demirbas.lastSearchUrl = null;
	uiState.demirbas.paginationLimit = 10;
}

function initGridFooter() {
	requestAnimationFrame(() => {
		const pagination = document.querySelector(".gridjs-pagination");
		if (!pagination) return;
		if (pagination.querySelector(".per-page-container")) return;

		const recordsInfo = document.createElement("div");
		recordsInfo.classList.add("grid-records-info");
		recordsInfo.textContent = "";
		pagination.insertBefore(recordsInfo, pagination.firstChild);

		const perPageContainer = document.createElement("div");
		perPageContainer.classList.add("per-page-container");

		const label = document.createElement("span");
		label.textContent = "Sayfa basi:";

		const select = document.createElement("select");
		select.classList.add("per-page");

		["5", "10", "25", "50", "100"].forEach((val) => {
			const opt = document.createElement("option");
			opt.value = val;
			opt.textContent = val;
			if (val === String(uiState.demirbas.paginationLimit)) opt.selected = true;
			select.appendChild(opt);
		});

		perPageContainer.appendChild(label);
		perPageContainer.appendChild(select);
		pagination.appendChild(perPageContainer);
	});
}
